import { type ThreeEvent, useThree, type Size } from '@react-three/fiber';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { DrawActiveShapeLines } from '@/components/property-selector/draw-components/draw-active-shape-lines';
import { TempDemarcation } from '@/components/property-selector/draw-components/draw-demarcation';
import { DrawDemarcationShapes } from '@/components/property-selector/draw-components/draw-demarcation-shapes';
import { DrawMap } from '@/components/property-selector/draw-components/draw-map';
import { DrawMarkers } from '@/components/property-selector/draw-components/draw-markers';
import { type Media } from '@/payload-types';
import { type Point, type PatchedPropertyUnit } from '@/types';

interface DrawObjectsProps {
  basemap?: Media | null;
  activeShapePoints?: Point[];
  nextPoint?: Point | null;
  tempDemarcations?: Point[][];
  showLegend?: boolean;
  colorsSettings: string;
  activeShapeColor?: string | number;
  demarcations?: PatchedPropertyUnit[];
  selectedDemarcationId?: string | null;
  hoverColor: string;
  availableColor: string;
  soldColor: string;
  onShapeClick?: (property: PatchedPropertyUnit) => void;
  onPointerDown?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
  onPointerUp?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
  onPointerMove?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
  onPointerOver?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
  onPointerOut?: (event: ThreeEvent<PointerEvent>, s: Size) => void;
}

const DrawMapObjectsBase: React.FC<DrawObjectsProps> = ({
  basemap,
  activeShapeColor,
  nextPoint,
  tempDemarcations,
  activeShapePoints,
  demarcations,
  showLegend = true,
  colorsSettings,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerOver,
  onPointerOut,
  onShapeClick,
  hoverColor,
  availableColor,
  soldColor,
  selectedDemarcationId,
}) => {
  const { size } = useThree();
  return (
    <ErrorBoundary fallback={null}>
      {demarcations && (
        <DrawDemarcationShapes
          size={size}
          selectedDemarcationId={selectedDemarcationId}
          showLegend={showLegend}
          colorsSettings={colorsSettings}
          onPointerOver={onPointerOver}
          onPointerOut={onPointerOut}
          properties={demarcations}
          onShapeClick={onShapeClick}
          hoverColor={hoverColor}
          availableColor={availableColor}
          soldColor={soldColor}
        />
      )}

      {tempDemarcations?.map((demarcation, index) => (
        <TempDemarcation
          key={index}
          size={size}
          shape={demarcation}
          onPointerOver={onPointerOver}
          onPointerOut={onPointerOut}
        />
      ))}

      {activeShapePoints && (
        <>
          <DrawActiveShapeLines
            points={activeShapePoints}
            nextPoint={nextPoint}
            color={activeShapeColor!}
            size={size}
          />
          <DrawMarkers points={activeShapePoints} size={size} />
        </>
      )}
      {basemap && (
        <DrawMap
          basemap={basemap}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerMove={onPointerMove}
        />
      )}
    </ErrorBoundary>
  );
};

export const DrawMapObjects = React.memo(DrawMapObjectsBase);
